import { tr } from '@/lib/i18n-core';
import { Wallet } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, put, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { useWorkingBranchId } from '@/lib/branch';
import { humanize, money, timeOnly, todayIso } from '@/lib/utils';
import {
  ConfirmDialog,
  DataTable,
  Dialog,
  FilterField,
  PageHeader,
  SearchFilterBar,
  Tabs,
} from '@/components/ui/layout';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  DatePicker,
  Field,
  Input,
  Select,
  StatusBadge,
  statusLabel,
} from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { LinkChips, type LinkChip } from '@/components/LinkChips';
import { payBillPath } from './pay-bill';
import { PayerDialog, RescheduleDialog, printSlip } from './FrontDeskDialogs';
import type { SlipData } from './token-slip';
import { DayBoard } from './DayBoard';
import { CancelAppointmentDialog } from './CancelAppointmentDialog';
import { AppointmentCalendarTab } from './calendar/AppointmentCalendarTab';
import { SellPackageDialog } from '../billing/PatientPackagesCard';
import { CheckUpBadge } from '../billing/PackageLineBadge';

interface AppointmentRow {
  id: string;
  slotAt: string;
  status: string;
  type: string;
  channel: string;
  tokenNo: string | null;
  reason: string | null;
  isFreeFollowUp: boolean;
  feeAlert: 'refund_due' | 'fee_review' | null;
  payer?: { id: string; name: string } | null;
  isWalkIn?: boolean;
  priority?: string;
  followUpPricePct?: number | null;
  encounterId?: string | null;
  invoiceId?: string | null;
  payInvoiceId?: string | null;
  packageName?: string | null;
  doctor: { id: string; name: string };
  patient: {
    id: string;
    mrn: string;
    fullName: string;
    ageLabel: string;
    sex: string;
    phone: string | null;
  };
}

type PageView = 'calendar' | 'list' | 'board';

const APPOINTMENT_PAGE = 100;

function pageViewOf(value: string | null): PageView {
  return value === 'list' || value === 'board' ? value : 'calendar';
}

function isoDateParam(value: string | null): string | null {
  return value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
}

/** Appointment desk: book, reschedule, mark arrival (SRS 6.2). */
export function AppointmentsPage() {
  const { can, hasModule } = useAuth();
  const maySellCheckUp = hasModule('billing') && can('invoice.create');
  const workingBranchId = useWorkingBranchId();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [params, setParams] = useSearchParams();
  const [date, setDate] = useState(isoDateParam(params.get('date')) ?? todayIso());
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState(params.get('q') ?? '');
  const [feeAlertsOnly, setFeeAlertsOnly] = useState(false);
  const view = pageViewOf(params.get('view'));
  const setView = (next: string) =>
    setParams(next === 'calendar' ? {} : { view: next }, { replace: true });
  const openBooking = () => {
    const search = new URLSearchParams();
    for (const key of ['patientId', 'doctorId', 'type']) {
      const value = params.get(key);
      if (value) search.set(key, value);
    }
    navigate(`/appointments/new${search.size ? `?${search}` : ''}`);
  };
  useEffect(() => {
    if (params.get('book') === '1') openBooking();
  });

  const appointments = useInfiniteQuery({
    queryKey: ['appointments', date, status, search, feeAlertsOnly],
    initialPageParam: '',
    queryFn: ({ pageParam }) =>
      get<{ data: AppointmentRow[]; nextCursor: string | null }>(
        `/appointments${qs({
          date: feeAlertsOnly ? undefined : date,
          status: status || undefined,
          q: search || undefined,
          feeAlert: feeAlertsOnly ? 'any' : undefined,
          limit: APPOINTMENT_PAGE,
          cursor: pageParam || undefined,
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: view === 'list',
  });
  const rows = appointments.data?.pages.flatMap((page) => page.data);

  const [cancelling, setCancelling] = useState<AppointmentRow | null>(null);
  const [sellingCheckUp, setSellingCheckUp] = useState<AppointmentRow | null>(null);
  const [markingNoShow, setMarkingNoShow] = useState<AppointmentRow | null>(null);
  const [resolving, setResolving] = useState<AppointmentRow | null>(null);
  const [moving, setMoving] = useState<AppointmentRow | null>(null);
  const [changingPayer, setChangingPayer] = useState<AppointmentRow | null>(null);
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['appointments'] });
    void queryClient.invalidateQueries({ queryKey: ['calendar'] });
    void queryClient.invalidateQueries({ queryKey: ['queue'] });
  };

  const arrive = useMutation({
    mutationFn: (id: string) => post<{ slip: SlipData }>(`/appointments/${id}/arrive`, {}),
    onSuccess: async (result) => {
      toast.success(
        tr('opd', 'arrived'),
        result.slip.tokenNo ? tr('opd', 'token_x', { tokenNo: result.slip.tokenNo }) : undefined,
      );
      refresh();
      await printSlip(result.slip.appointmentId, result.slip);
    },
    onError: (error) => toast.error(tr('opd', 'could_not_mark_the_arrival'), describeError(error)),
  });

  const changeStatus = useMutation({
    mutationFn: ({ id, next }: { id: string; next: string }) =>
      put<{ cancelledInvoices?: number; refundNeeded?: boolean }>(`/appointments/${id}/status`, {
        status: next,
      }),
    onSuccess: (result, variables) => {
      const what = variables.next === 'no_show' ? 'Marked as not seen' : 'Appointment updated';
      if (result.refundNeeded) {
        toast.warning(
          tr('opd', 'x_a_refund_is_due', { what: what }),
          tr('opd', 'the_fee_was_already_paid_the_appointment_is'),
        );
      } else if (result.cancelledInvoices) {
        toast.success(
          what,
          tr('opd', 'x_unpaid_fee_invoice_s_were_cancelled_with_it', {
            cancelledInvoices: result.cancelledInvoices,
          }),
        );
      } else {
        toast.success(what);
      }
      refresh();
    },
    onError: (error) =>
      toast.error(tr('opd', 'could_not_update_the_appointment'), describeError(error)),
  });

  const rowLinks = (row: AppointmentRow): LinkChip[] => [
    ...(row.encounterId && can('encounter.read', 'encounter.read.own', 'encounter.write')
      ? [{ to: `/consultations/${row.encounterId}`, label: tr('opd', 'consultation') }]
      : []),
    ...(row.invoiceId && can('invoice.read')
      ? [{ to: `/billing/invoices/${row.invoiceId}`, label: tr('opd', 'invoice') }]
      : []),
  ];

  return (
    <>
      <PageHeader
        title={tr('opd', 'appointments')}
        description={tr('opd', 'bookings_for_the_selected_day')}
        actions={
          <div className="flex gap-2">
            {can('appointment.manage', 'token.issue') && (
              <Button variant="outline" onClick={() => navigate('/appointments/walk-in')}>
                {tr('opd', 'walk_in')}
              </Button>
            )}
            {can('appointment.manage') && (
              <Button onClick={openBooking}>{tr('opd', 'book_appointment')}</Button>
            )}
          </div>
        }
      />

      <Tabs
        tabs={[
          { id: 'calendar', label: tr('opd', 'calendar') },
          { id: 'list', label: tr('opd', 'list') },
          { id: 'board', label: tr('opd', 'day_board') },
        ]}
        active={view}
        onChange={setView}
      />

      {view === 'calendar' && <AppointmentCalendarTab />}

      {view === 'board' && (
        <>
          <div className="mb-3 flex flex-wrap gap-2">
            <DatePicker value={date} onChange={setDate} className="w-40" />
          </div>
          <DayBoard branchId={workingBranchId} date={date} />
        </>
      )}

      {view === 'list' && (
        <>
          <SearchFilterBar
            search={{
              value: search,
              onChange: setSearch,
              placeholder: tr('opd', 'search_name_mrn_or_phone'),
            }}
            filters={
              <>
                <FilterField label={tr('opd', 'date')}>
                  <DatePicker value={date} onChange={setDate} className="w-40" />
                </FilterField>
                <FilterField label={tr('opd', 'status')}>
                  <Select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-44"
                  >
                    <option value="">{tr('opd', 'all_statuses')}</option>
                    {[
                      'booked',
                      'confirmed',
                      'arrived',
                      'in_consultation',
                      'completed',
                      'no_show',
                      'cancelled',
                    ].map((s) => (
                      <option key={s} value={s}>
                        {statusLabel(s)}
                      </option>
                    ))}
                  </Select>
                </FilterField>
                <FilterField>
                  <Checkbox
                    checked={feeAlertsOnly}
                    onCheckedChange={setFeeAlertsOnly}
                    label={tr('opd', 'fee_alerts_only_any_date')}
                  />
                </FilterField>
              </>
            }
            onReset={
              status || search || feeAlertsOnly || date !== todayIso()
                ? () => {
                    setStatus('');
                    setSearch('');
                    setFeeAlertsOnly(false);
                    setDate(todayIso());
                  }
                : undefined
            }
          />

          <DataTable
            rows={rows}
            loading={appointments.isLoading}
            refreshing={appointments.isFetching && !appointments.isFetchingNextPage}
            partial={Boolean(appointments.hasNextPage)}
            error={appointments.error}
            rowKey={(row) => row.id}
            empty={tr('opd', 'no_appointments_for_this_day')}
            columns={[
              {
                header: tr('opd', 'time'),
                width: '90px',
                sort: (row) => row.slotAt,
                cell: (row) => <span className="tabular">{timeOnly(row.slotAt)}</span>,
              },
              {
                header: tr('opd', 'token'),
                width: '70px',
                sort: (row) => row.tokenNo,
                cell: (row) => <span className="tabular">{row.tokenNo ?? '—'}</span>,
              },
              {
                header: tr('opd', 'patient'),
                sort: (row) => row.patient.fullName,
                cell: (row) => (
                  <div>
                    <p className="font-medium">{row.patient.fullName}</p>
                    <p className="text-xs text-muted-foreground">
                      {row.patient.mrn} · {row.patient.ageLabel} · {humanize(row.patient.sex)}
                    </p>
                  </div>
                ),
              },
              {
                header: tr('opd', 'doctor'),
                sort: (row) => row.doctor.name,
                cell: (row) => row.doctor.name,
              },
              {
                header: tr('opd', 'visit'),
                cell: (row) => (
                  <div className="flex gap-1">
                    <Badge variant="secondary">{humanize(row.type)}</Badge>
                    {row.isFreeFollowUp && <Badge variant="success">{tr('opd', 'free')}</Badge>}
                    {!row.isFreeFollowUp &&
                      row.followUpPricePct != null &&
                      row.followUpPricePct > 0 && (
                        <Badge variant="secondary">{row.followUpPricePct}%</Badge>
                      )}
                    {row.isWalkIn && <Badge variant="muted">walk-in</Badge>}
                    {row.packageName && <CheckUpBadge name={row.packageName} />}
                    {row.priority === 'urgent' && (
                      <Badge variant="destructive">{tr('opd', 'see_first')}</Badge>
                    )}
                    {row.payer && <Badge variant="outline">{row.payer.name}</Badge>}
                    {row.feeAlert === 'refund_due' && (
                      <Badge variant="destructive">{tr('opd', 'refund_due')}</Badge>
                    )}
                    {row.feeAlert === 'fee_review' && (
                      <Badge variant="warning">{tr('opd', 'doctor_changed_check_fee')}</Badge>
                    )}
                  </div>
                ),
              },
              {
                header: tr('opd', 'status'),
                sort: (row) => row.status,
                cell: (row) => <StatusBadge status={row.status} />,
              },
              { header: '', cell: (row) => <LinkChips links={rowLinks(row)} /> },
              {
                header: '',
                align: 'right',
                cell: (row) => (
                  <div className="flex justify-end gap-1">
                    {row.payInvoiceId && can('payment.collect') && (
                      <Button
                        size="sm"
                        variant="success"
                        onClick={() => navigate(payBillPath(row.payInvoiceId as string, `/appointments${qs({ view: 'list', date })}`))}
                      >
                        <Wallet className="h-3.5 w-3.5" aria-hidden /> {tr('opd', 'pay_bill')}
                      </Button>
                    )}
                    {can('appointment.manage', 'token.issue') && (
                      <>
                        {['booked', 'confirmed'].includes(row.status) && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => arrive.mutate(row.id)}
                              loading={arrive.isPending && arrive.variables === row.id}
                            >
                              {tr('opd', 'arrived')}
                            </Button>
                            {row.status === 'booked' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() =>
                                  changeStatus.mutate({ id: row.id, next: 'confirmed' })
                                }
                              >
                                {tr('opd', 'confirm')}
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setMarkingNoShow(row)}
                            >
                              {tr('opd', 'no_show')}
                            </Button>
                          </>
                        )}
                        {row.status === 'arrived' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => void printSlip(row.id)}
                            >
                              {tr('opd', 'slip')}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setMarkingNoShow(row)}
                            >
                              {tr('opd', 'left_without_being_seen')}
                            </Button>
                          </>
                        )}
                        {['booked', 'confirmed', 'no_show'].includes(row.status) &&
                          can('appointment.manage') && (
                            <Button size="sm" variant="ghost" onClick={() => setMoving(row)}>
                              {tr('opd', 'move')}
                            </Button>
                          )}
                        {!['completed', 'cancelled', 'no_show'].includes(row.status) &&
                          can('appointment.manage') && (
                            <Button size="sm" variant="ghost" onClick={() => setChangingPayer(row)}>
                              {tr('opd', 'payer')}
                            </Button>
                          )}
                        {row.feeAlert && can('appointment.manage') && (
                          <Button size="sm" variant="outline" onClick={() => setResolving(row)}>
                            {tr('opd', 'settle_fee_alert')}
                          </Button>
                        )}
                        {['booked', 'confirmed', 'arrived', 'in_consultation'].includes(row.status) && !row.packageName && maySellCheckUp && (
                          <Button size="sm" variant="ghost" onClick={() => setSellingCheckUp(row)}>
                            {tr('billing', 'add_check_up')}
                          </Button>
                        )}
                        {['booked', 'confirmed', 'arrived'].includes(row.status) && (
                          <Button size="sm" variant="ghost" onClick={() => setCancelling(row)}>
                            {tr('opd', 'cancel')}
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                ),
              },
            ]}
          />
          {appointments.hasNextPage && (
            <div className="mt-3 flex justify-center">
              <Button variant="outline" onClick={() => void appointments.fetchNextPage()} loading={appointments.isFetchingNextPage}>
                {tr('opd', 'load_more')}
              </Button>
            </div>
          )}
        </>
      )}

      {markingNoShow && (
        <ConfirmDialog
          title={markingNoShow.status === 'arrived' ? tr('opd', 'left_without_being_seen') : tr('opd', 'no_show')}
          description={`${markingNoShow.patient.fullName} · ${markingNoShow.doctor.name}`}
          destructive
          confirmLabel={markingNoShow.status === 'arrived' ? tr('opd', 'mark_left_without_being_seen') : tr('opd', 'mark_no_show')}
          onConfirm={() => changeStatus.mutateAsync({ id: markingNoShow.id, next: 'no_show' })}
          onClose={() => setMarkingNoShow(null)}
        >
          <p className="text-sm text-muted-foreground">{tr('opd', 'no_show_money_effect')}</p>
        </ConfirmDialog>
      )}

      {moving && (
        <RescheduleDialog
          appointment={moving}
          onClose={() => setMoving(null)}
          onDone={() => {
            setMoving(null);
            refresh();
          }}
        />
      )}

      {changingPayer && (
        <PayerDialog
          appointment={changingPayer}
          onClose={() => setChangingPayer(null)}
          onDone={() => {
            setChangingPayer(null);
            refresh();
          }}
        />
      )}

      {cancelling && (
        <CancelAppointmentDialog
          appointment={cancelling}
          onClose={() => setCancelling(null)}
          onDone={() => {
            setCancelling(null);
            refresh();
          }}
        />
      )}

      {sellingCheckUp && (
        <SellPackageDialog
          patientId={sellingCheckUp.patient.id}
          kind="health_checkup"
          admissionId={null}
          appointmentId={sellingCheckUp.id}
          branchId={null}
          onClose={() => setSellingCheckUp(null)}
          onSold={() => {
            setSellingCheckUp(null);
            refresh();
          }}
        />
      )}

      {resolving && (
        <ResolveFeeAlertDialog
          appointment={resolving}
          onClose={() => setResolving(null)}
          onDone={() => {
            setResolving(null);
            void queryClient.invalidateQueries({ queryKey: ['appointments'] });
          }}
        />
      )}
    </>
  );
}

interface FeeAlertMoney {
  feeAlert: 'refund_due' | 'fee_review' | null;
  paidMinor: number;
  refundedMinor: number;
  creditedMinor: number;
  owedBackMinor: number;
  refunds: { id: string; status: string; amountMinor: number }[];
  creditNotes: { id: string; status: string; netMinor: number }[];
}

function ResolveFeeAlertDialog({
  appointment,
  onClose,
  onDone,
}: {
  appointment: AppointmentRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const [choice, setChoice] = useState('');
  const [waiveReason, setWaiveReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const visit = useQuery({
    queryKey: ['fee-alert', appointment.id],
    queryFn: () => get<FeeAlertMoney>(`/appointments/${appointment.id}/fee-alert`),
  });

  const refunds = (visit.data?.refunds ?? []).filter((refund) =>
    ['approved', 'paid'].includes(refund.status),
  );
  const creditNotes = (visit.data?.creditNotes ?? []).filter((note) => note.status === 'applied');
  const mayWaive = can('appointment.fee_alert.waive');

  const resolve = useMutation({
    mutationFn: () => {
      const [kind, id] = choice.split(':');
      const body =
        kind === 'refund'
          ? { refundId: id }
          : kind === 'credit'
            ? { creditNoteId: id }
            : { waived: true, reason: waiveReason.trim() };
      return post(`/appointments/${appointment.id}/fee-alert/resolve`, body);
    },
    onSuccess: () => {
      toast.success(tr('opd', 'fee_alert_settled'));
      onDone();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ready = choice === 'waive' ? waiveReason.trim().length >= 10 : Boolean(choice);

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('opd', 'settle_the_fee_alert')}
      description={`${appointment.patient.fullName} · ${appointment.feeAlert === 'refund_due' ? 'refund due' : 'doctor changed — check the fee'}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('opd', 'close')}
          </Button>
          <Button onClick={() => resolve.mutate()} loading={resolve.isPending} disabled={!ready}>
            {tr('opd', 'settle')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {visit.data && (
          <p className="text-sm text-muted-foreground">
            {tr('opd', 'paid')} {money(visit.data.paidMinor)} {tr('opd', 'refunded')}{' '}
            {money(visit.data.refundedMinor)} {tr('opd', 'credited')}{' '}
            {money(visit.data.creditedMinor)}
          </p>
        )}
        {visit.data && refunds.length === 0 && creditNotes.length === 0 && (
          <Alert variant="warning">
            {tr('opd', 'no_approved_refund_or_issued_credit_note_is')}
          </Alert>
        )}
        <div className="space-y-2" role="radiogroup" aria-label={tr('opd', 'settled_by')}>
          {refunds.map((refund) => (
            <label key={refund.id} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="fee-alert"
                checked={choice === `refund:${refund.id}`}
                onChange={() => setChoice(`refund:${refund.id}`)}
              />
              {tr('opd', 'refund_of')} {money(refund.amountMinor)} ({statusLabel(refund.status)})
            </label>
          ))}
          {creditNotes.map((note) => (
            <label key={note.id} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="fee-alert"
                checked={choice === `credit:${note.id}`}
                onChange={() => setChoice(`credit:${note.id}`)}
              />
              {tr('opd', 'credit_note_for')} {money(note.netMinor)}
            </label>
          ))}
          {mayWaive && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="fee-alert"
                checked={choice === 'waive'}
                onChange={() => setChoice('waive')}
              />
              {tr('opd', 'nothing_is_owed_back_waive_the_alert')}
            </label>
          )}
        </div>
        {choice === 'waive' && (
          <Field
            label={tr('opd', 'why_nothing_is_owed_back')}
            required
            hint={tr('opd', 'at_least_ten_characters_kept_with_the')}
          >
            <Input
              value={waiveReason}
              onChange={(e) => setWaiveReason(e.target.value)}
              maxLength={400}
            />
          </Field>
        )}
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}
